/**
 * NativeSSOPage — Page autonome complète pour @ollaid/native-sso
 * Design aligné sur le parcours Native SSO (fond primary, card blanche, ShieldCheck branding)
 * 
 * @version 2.7.9
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { LoginModal } from './LoginModal';
import { SignupModal } from './SignupModal';
import { OnboardingModal } from './OnboardingModal';
import PasswordRedirectModal from './PasswordRedirectModal';
import AppsLogoSlider from './AppsLogoSlider';
import { DebugPanel, type DebugOnboardingPreset } from './DebugPanel';
import ollaidLogo from '../logo/ollaid.png';
import { Button } from './ui';
import {
  getNativeStorage,
  setNativeStorage,
  STORAGE_KEYS,
  logout,
  repairNativeSsoStorage,
  getProfilePromptState,
  markProfilePromptComplete,
  setProfilePromptState,
  snoozeProfilePrompt,
} from '../services/api';
import { nativeAuthService } from '../services/nativeAuth';
import { profileService } from '../services/profile';
import { useNativeAuth } from '../hooks/useNativeAuth';
import type { UserInfos, NativeUser } from '../types/native';
import type { NativeStorageAdapter } from '../services/api';

export interface NativeSSOPageProps {
  saasApiUrl: string;
  iamApiUrl: string;
  onLoginSuccess?: (token: string, user: UserInfos) => void;
  onLogout?: () => void;
  onOnboardingComplete?: (data: {
    name?: string;
    image_url?: string;
    ccphone?: string;
    phone?: string;
    email?: string;
    address?: string;
    town?: string;
    country?: string;
    user_infos?: UserInfos;
  }) => void;
  accountType?: 'user' | 'client';
  configPrefix?: string;
  title?: string;
  description?: string;
  logoUrl?: string;
  hideFooter?: boolean;
  homeUrl?: string;
  redirectAfterLogin?: string;
  redirectAfterLogout?: string;
  storage?: NativeStorageAdapter;
}

type ModalState = 'none' | 'login' | 'signup';

const PROFILE_PROMPT_DELAY_MS = 5 * 60 * 1000;
const PROFILE_PROMPT_SNOOZE_MS = 8 * 60 * 60 * 1000;
const PROFILE_SYNC_INTERVAL_MS = 30 * 60 * 1000;
const PROFILE_FETCH_RETRY_DELAY_MS = 1200;
const PROFILE_FETCH_MAX_ATTEMPTS = 3;

const COLORS = {
  primary: '#002147',
  primaryForeground: '#ffffff',
  accent: '#e8430a',
  card: '#ffffff',
  cardForeground: '#1a2332',
  muted: '#6b7280',
  border: '#e5e7eb',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
} as const;

const SSO_VERSION = '2.8.2';

const LIGHT_VARS: Record<string, string> = {
  "--background": "40 20% 96%",
  "--foreground": "220 15% 15%",
  "--card": "0 0% 100%",
  "--card-foreground": "220 15% 15%",
  "--popover": "0 0% 100%",
  "--popover-foreground": "220 15% 15%",
  "--primary": "209 100% 13%",
  "--primary-foreground": "0 0% 100%",
  "--secondary": "40 15% 92%",
  "--secondary-foreground": "220 15% 25%",
  "--muted": "40 10% 90%",
  "--muted-foreground": "220 10% 50%",
  "--accent": "16 99% 52%",
  "--accent-foreground": "0 0% 100%",
  "--destructive": "0 84.2% 60.2%",
  "--destructive-foreground": "210 40% 98%",
  "--border": "40 15% 88%",
  "--input": "40 15% 88%",
  "--ring": "209 100% 13%",
};

function safeRedirect(target?: string): boolean {
  if (!target) return false;

  if (target.startsWith('/')) {
    window.location.href = target;
    return true;
  }

  try {
    const url = new URL(target, window.location.origin);
    if (url.origin === window.location.origin) {
      window.location.href = url.toString();
      return true;
    }
  } catch {
    // ignore invalid URL
  }

  return false;
}

// Shield check SVG inline
const ShieldCheckIcon = ({ size = 24, color = COLORS.accent }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

function needsOnboarding(user: NativeUser | UserInfos): boolean {
  return !user.name?.trim() || !user.image_url || !user.phone || !user.email || !user.address?.trim() || !user.town?.trim() || !user.country?.trim();
}

function isProfileComplete(user: NativeUser | UserInfos): boolean {
  return Boolean(
    user.name?.trim()
    && user.image_url
    && user.phone
    && user.email
    && user.address?.trim()
    && user.town?.trim()
    && user.country?.trim()
  );
}

// Gradient border animation keyframes (injected once)
const GRADIENT_STYLE_ID = 'ollaid-sso-gradient-anim';
function injectGradientAnimation() {
  if (document.getElementById(GRADIENT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = GRADIENT_STYLE_ID;
  style.textContent = `
    @keyframes ollaid-border-gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes ollaid-marquee {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
  `;
  document.head.appendChild(style);
}

/** Top branding: ShieldCheck + iam.ollaid.com + subtitle */
const TopBranding = ({ subtitle }: { subtitle: string }) => (
  <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
      <ShieldCheckIcon size={28} color={COLORS.accent} />
      <span style={{ fontSize: '1.25rem', fontWeight: 700, color: COLORS.primaryForeground }}>
        iam.<span style={{ color: COLORS.accent }}>ollaid</span>.com
      </span>
    </div>
    <p style={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
      {subtitle}
    </p>
  </div>
);

/** Footer: Ollaid logo + copyright */
const Footer = ({ hideFooter }: { hideFooter: boolean }) => (
  <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
    <img src={ollaidLogo} alt="Ollaid" style={{ height: '28px', margin: '0 auto 0.5rem' }} />
    {!hideFooter && (
      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
        OLLAID SSO v{SSO_VERSION} · © 2026 iam.ollaid.com propulsé par <span style={{ fontWeight: 500 }}>ollaid</span>
      </p>
    )}
  </div>
);

export function NativeSSOPage({
  saasApiUrl,
  iamApiUrl,
  onLoginSuccess,
  onLogout,
  onOnboardingComplete,
  accountType = 'user',
  configPrefix = 'iam',
  title = 'Un compte pour toutes vos applications',
  description = 'Connectez-vous avec votre compte Ollaid pour accéder à toutes les applications partenaires.',
  logoUrl,
  hideFooter = false,
  homeUrl,
  redirectAfterLogin,
  redirectAfterLogout,
  storage,
}: NativeSSOPageProps) {
  if (storage) {
    setNativeStorage(storage);
  }

  const [modal, setModal] = useState<ModalState>('none');
  const [loginInitialPhone, setLoginInitialPhone] = useState<string | undefined>();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pendingSession, setPendingSession] = useState<{ token: string; user: NativeUser; sourceUser: NativeUser } | null>(null);
  const [debugOnboardingState, setDebugOnboardingState] = useState<{
    token: string;
    user: NativeUser;
    sourceUser: NativeUser;
    preset: DebugOnboardingPreset;
    variant: 'edit';
  } | null>(null);
  const [passwordRedirectOpen, setPasswordRedirectOpen] = useState(false);
  const [redirectingTarget, setRedirectingTarget] = useState<string | null>(null);
  const [redirectingReason, setRedirectingReason] = useState<'login' | 'logout' | 'already-authenticated' | null>(null);
  const [session, setSession] = useState<{ token: string; user: NativeUser } | null>(() => {
    try {
      const storage = getNativeStorage();
      const token = storage.getItem(STORAGE_KEYS.AUTH_TOKEN) || storage.getItem(STORAGE_KEYS.TOKEN);
      const userStr = storage.getItem(STORAGE_KEYS.USER);
      if (token && userStr) return { token, user: JSON.parse(userStr) };
    } catch {
      return null;
    }
    return null;
  });

  const { isDebug: resolvedDebug } = useNativeAuth({ saasApiUrl, iamApiUrl, configPrefix, autoLoadCredentials: true, storage });
  const onboardingPromptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onboardingRecheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const profileSyncTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const profileHydrationRunningRef = useRef(false);
  const profileHydrationRequestedRef = useRef<string | null>(null);
  const sessionRef = useRef(session);
  const [profileHydrating, setProfileHydrating] = useState(false);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    const repairResult = repairNativeSsoStorage();
    if (repairResult.cleaned) {
      setSession(null);

      const isDev = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
      if (isDev) {
        console.warn('🔧 [NativeSSOPage] Storage SSO réparé', repairResult.reason);
      }
    }
  }, []);

  useEffect(() => {
    if (!redirectingTarget) return;

    const timer = window.setTimeout(() => {
      const redirected = safeRedirect(redirectingTarget);
      if (!redirected) {
        setRedirectingTarget(null);
        setRedirectingReason(null);
      }
    }, 200);

    return () => window.clearTimeout(timer);
  }, [redirectingTarget]);

  const clearOnboardingTimers = useCallback(() => {
    if (onboardingPromptTimerRef.current) {
      clearTimeout(onboardingPromptTimerRef.current);
      onboardingPromptTimerRef.current = null;
    }
    if (onboardingRecheckTimerRef.current) {
      clearTimeout(onboardingRecheckTimerRef.current);
      onboardingRecheckTimerRef.current = null;
    }
  }, []);

  const persistSessionUser = useCallback((token: string, user: NativeUser) => {
    const storage = getNativeStorage();
    setSession({ token, user });
    storage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    storage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  }, []);

  const extractUserInfos = useCallback((payload: unknown): Partial<NativeUser> | null => {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload as Record<string, unknown>;
    const candidate = (data.user_infos && typeof data.user_infos === 'object')
      ? data.user_infos as Record<string, unknown>
      : data.user && typeof data.user === 'object'
        ? data.user as Record<string, unknown>
        : data;

    return {
      reference: typeof candidate.reference === 'string' ? candidate.reference : undefined,
      name: typeof candidate.name === 'string' ? candidate.name : undefined,
      email: typeof candidate.email === 'string' ? candidate.email : undefined,
      ccphone: typeof candidate.ccphone === 'string' ? candidate.ccphone : undefined,
      phone: typeof candidate.phone === 'string' ? candidate.phone : undefined,
      address: typeof candidate.address === 'string' ? candidate.address : undefined,
      town: typeof candidate.town === 'string' ? candidate.town : undefined,
      country: typeof candidate.country === 'string' ? candidate.country : undefined,
      image_url: typeof candidate.image_url === 'string' ? candidate.image_url : undefined,
      auth_2fa: typeof candidate.auth_2fa === 'boolean' ? candidate.auth_2fa : undefined,
      alias_reference: typeof candidate.alias_reference === 'string' ? candidate.alias_reference : undefined,
    };
  }, []);

  const refreshSessionProfile = useCallback(async (force = false): Promise<NativeUser | null> => {
    const activeSession = sessionRef.current;
    if (!activeSession?.token) return null;
    if (profileHydrationRunningRef.current && !force) return activeSession.user;
    if (profileHydrationRequestedRef.current === activeSession.token && !force) return activeSession.user;

    profileHydrationRunningRef.current = true;
    profileHydrationRequestedRef.current = activeSession.token;
    setProfileHydrating(true);
    try {
      let lastError: unknown = null;
      for (let attempt = 1; attempt <= PROFILE_FETCH_MAX_ATTEMPTS; attempt += 1) {
        try {
          const response = await profileService.getProfile();
          const mergedInfos = extractUserInfos(response) || {};
          const mergedUser = { ...activeSession.user, ...mergedInfos } as NativeUser;
          persistSessionUser(activeSession.token, mergedUser);
          return mergedUser;
        } catch (error) {
          lastError = error;
          if (attempt < PROFILE_FETCH_MAX_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, PROFILE_FETCH_RETRY_DELAY_MS * attempt));
          }
        }
      }

      const isDev = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
      if (isDev) {
        console.warn('Profile hydration failed after retries', lastError);
      }
      return activeSession.user;
    } finally {
      profileHydrationRunningRef.current = false;
      setProfileHydrating(false);
    }
  }, [persistSessionUser, extractUserInfos]);

  const makeDebugOnboardingUser = useCallback((baseUser: NativeUser, preset: DebugOnboardingPreset): NativeUser => {
    const normalized: NativeUser = { ...baseUser };

    if (preset === 'current') return normalized;
    if (preset === 'photo') return { ...normalized, image_url: '' };
    if (preset === 'phone') return { ...normalized, phone: '', ccphone: '' };
    if (preset === 'email') return { ...normalized, email: '' };
    return { ...normalized, image_url: '', phone: '', ccphone: '', email: '' };
  }, []);

  const syncProfilePrompt = useCallback((currentSession: { token: string; user: NativeUser } | null) => {
    clearOnboardingTimers();

    if (!currentSession) {
      setPendingSession(null);
      setShowOnboarding(false);
      return;
    }

    const promptState = getProfilePromptState();
    const now = Date.now();
    const onboardingVisible = showOnboarding || Boolean(pendingSession || debugOnboardingState);

    if (promptState.lastStatus === true || isProfileComplete(currentSession.user)) {
      markProfilePromptComplete();
      if (!onboardingVisible) {
        setPendingSession(null);
        setShowOnboarding(false);
      }
      return;
    }

    setPendingSession({ ...currentSession, sourceUser: currentSession.user });

    const scheduleOpen = () => {
      onboardingPromptTimerRef.current = setTimeout(() => {
        const latestPromptState = getProfilePromptState();
        if (latestPromptState.lastStatus === true) return;
        if (!currentSession) return;
        if (!isProfileComplete(currentSession.user)) {
          setShowOnboarding(true);
        }
      }, PROFILE_PROMPT_DELAY_MS);
    };

    if (promptState.recheckAt && promptState.recheckAt > now) {
      onboardingRecheckTimerRef.current = setTimeout(() => {
        const latestPromptState = getProfilePromptState();
        if (latestPromptState.lastStatus === true) return;
        if (!currentSession) return;
        if (!isProfileComplete(currentSession.user)) {
          scheduleOpen();
        }
      }, promptState.recheckAt - now);
      return;
    }

    scheduleOpen();
  }, [clearOnboardingTimers, showOnboarding, pendingSession, debugOnboardingState]);

  // Force light mode + inject gradient animation
  useEffect(() => {
    injectGradientAnimation();
    const root = document.documentElement;
    const originalValues: Record<string, string> = {};
    Object.keys(LIGHT_VARS).forEach((key) => { originalValues[key] = root.style.getPropertyValue(key); });
    Object.entries(LIGHT_VARS).forEach(([key, value]) => { root.style.setProperty(key, value); });
    root.classList.add('light');
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
    const meta = document.createElement('meta');
    meta.name = 'color-scheme';
    meta.content = 'light';
    document.head.appendChild(meta);
    return () => {
      Object.entries(originalValues).forEach(([key, value]) => {
        if (value) root.style.setProperty(key, value);
        else root.style.removeProperty(key);
      });
      root.classList.remove('light');
      root.style.colorScheme = '';
      meta.remove();
    };
  }, []);

  const openLogin = useCallback(() => setModal('login'), []);
  const openSignup = useCallback(() => setModal('signup'), []);
  const closeModal = useCallback(() => { setModal('none'); setLoginInitialPhone(undefined); }, []);
  const switchToSignup = useCallback(() => { setModal('none'); setLoginInitialPhone(undefined); setTimeout(() => setModal('signup'), 150); }, []);
  const switchToLogin = useCallback(() => { setModal('none'); setLoginInitialPhone(undefined); setTimeout(() => setModal('login'), 150); }, []);
  const switchToLoginWithPhone = useCallback((phone: string) => {
    setModal('none');
    setLoginInitialPhone(phone);
    setTimeout(() => setModal('login'), 150);
  }, []);

  const beginRedirect = useCallback((target?: string, reason: 'login' | 'logout' | 'already-authenticated' = 'login') => {
    if (!target) return false;
    setRedirectingReason(reason);
    setRedirectingTarget(target);
    return true;
  }, []);

  const handleLoginSuccess = useCallback((token: string, user: UserInfos) => {
    const userObj: NativeUser = {
      reference: '',
      name: user.name,
      email: user.email || undefined,
      phone: user.phone,
      ccphone: user.ccphone,
      image_url: user.image_url,
    };
    setModal('none');
    persistSessionUser(token, userObj);
    syncProfilePrompt({ token, user: userObj });
    void refreshSessionProfile(true);

    if (!needsOnboarding(userObj)) {
      markProfilePromptComplete();
      try {
        onLoginSuccess?.(token, user);
      } catch (callbackError) {
        console.error('Erreur dans le callback onLoginSuccess du wrapper SSO', callbackError);
      }
      beginRedirect(redirectAfterLogin, 'login');
    }
  }, [onLoginSuccess, redirectAfterLogin, persistSessionUser, syncProfilePrompt, refreshSessionProfile, beginRedirect]);

  const handleOnboardingComplete = useCallback((data: {
    name?: string;
    image_url?: string;
    ccphone?: string;
    phone?: string;
    email?: string;
    address?: string;
    town?: string;
    country?: string;
    user_infos?: UserInfos;
  }) => {
    const activeSession = debugOnboardingState || pendingSession;
    if (!activeSession) return;
    const updatedUser = { ...activeSession.sourceUser, ...(data.user_infos || data) } as NativeUser;
    persistSessionUser(activeSession.token, updatedUser);
    onOnboardingComplete?.({
      name: updatedUser.name,
      image_url: updatedUser.image_url,
      ccphone: updatedUser.ccphone || undefined,
      phone: updatedUser.phone || undefined,
      email: updatedUser.email || undefined,
      address: updatedUser.address || undefined,
      town: updatedUser.town || undefined,
      country: updatedUser.country || undefined,
        user_infos: {
          reference: updatedUser.reference,
          name: updatedUser.name,
          email: updatedUser.email || null,
        ccphone: updatedUser.ccphone,
        phone: updatedUser.phone,
        address: updatedUser.address,
        town: updatedUser.town,
          country: updatedUser.country,
          image_url: updatedUser.image_url,
          auth_2fa: updatedUser.auth_2fa,
          alias_reference: updatedUser.alias_reference,
        },
      });
    if (!debugOnboardingState) {
      markProfilePromptComplete();
    }
    void refreshSessionProfile(true);
  }, [pendingSession, debugOnboardingState, onOnboardingComplete, persistSessionUser, refreshSessionProfile]);

  const handleOnboardingUserInfosSync = useCallback((payload: {
    source: 'hydration' | 'update' | 'avatar' | 'contact';
    user_infos: UserInfos;
  }) => {
    const activeSession = debugOnboardingState || pendingSession;
    if (!activeSession) return;

    const syncedUser = {
      ...activeSession.sourceUser,
      ...payload.user_infos,
    } as NativeUser;

    persistSessionUser(activeSession.token, syncedUser);

    if (debugOnboardingState) {
      setDebugOnboardingState(prev => prev ? {
        ...prev,
        user: syncedUser,
        sourceUser: syncedUser,
      } : prev);
    } else {
      setPendingSession(prev => prev ? {
        ...prev,
        user: syncedUser,
        sourceUser: syncedUser,
      } : prev);
    }
  }, [debugOnboardingState, pendingSession, persistSessionUser]);

  const handleOnboardingSkip = useCallback(() => {
    const activeSession = debugOnboardingState || pendingSession;
    if (!activeSession) return;
    setShowOnboarding(false);
    setPendingSession(null);
    setDebugOnboardingState(null);
    if (!debugOnboardingState) {
      persistSessionUser(activeSession.token, activeSession.user);
      snoozeProfilePrompt(PROFILE_PROMPT_SNOOZE_MS / (60 * 60 * 1000));
      try {
        onLoginSuccess?.(activeSession.token, activeSession.user as unknown as UserInfos);
      } catch (callbackError) {
        console.error('Erreur dans le callback onLoginSuccess du wrapper SSO', callbackError);
      }
      beginRedirect(redirectAfterLogin, 'login');
      return;
    }
  }, [pendingSession, debugOnboardingState, onLoginSuccess, redirectAfterLogin, persistSessionUser, beginRedirect]);

  const handleOnboardingDismiss = useCallback(() => {
    setShowOnboarding(false);
    setPendingSession(null);
    setDebugOnboardingState(null);
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    setSession(null);
    setPendingSession(null);
    setDebugOnboardingState(null);
    clearOnboardingTimers();
    onLogout?.();
    beginRedirect(redirectAfterLogout, 'logout');
  }, [onLogout, redirectAfterLogout, clearOnboardingTimers, beginRedirect]);

  const openDebugLogin = useCallback(() => {
    clearOnboardingTimers();
    setShowOnboarding(false);
    setPendingSession(null);
    setDebugOnboardingState(null);
    setModal('login');
  }, [clearOnboardingTimers]);

  const openDebugSignup = useCallback(() => {
    clearOnboardingTimers();
    setShowOnboarding(false);
    setPendingSession(null);
    setDebugOnboardingState(null);
    setModal('signup');
  }, [clearOnboardingTimers]);

  useEffect(() => {
    if (!session?.token || !redirectAfterLogin || redirectingTarget) return;
    beginRedirect(redirectAfterLogin, 'already-authenticated');
  }, [session?.token, redirectAfterLogin, redirectingTarget, beginRedirect]);

  const openDebugOnboarding = useCallback((preset: DebugOnboardingPreset = 'current') => {
    if (!session) return;
    clearOnboardingTimers();
    setModal('none');
    const simulatedUser = makeDebugOnboardingUser(session.user, preset);
    setDebugOnboardingState({
      token: session.token,
      user: simulatedUser,
      sourceUser: session.user,
      preset,
      variant: 'edit',
    });
    setPendingSession(null);
    setShowOnboarding(true);
  }, [session, clearOnboardingTimers, makeDebugOnboardingUser]);

  const resetDebugProfilePrompt = useCallback(() => {
    setProfilePromptState(false, Date.now() + PROFILE_PROMPT_SNOOZE_MS);
    if (session) {
      clearOnboardingTimers();
      setDebugOnboardingState(null);
      setPendingSession({ ...session, sourceUser: session.user });
      setShowOnboarding(true);
    }
  }, [session, clearOnboardingTimers]);

  const openDebugPassword = useCallback(() => {
    setPasswordRedirectOpen(true);
  }, []);

  useEffect(() => {
    syncProfilePrompt(session);
    return () => {
      clearOnboardingTimers();
    };
  }, [session, syncProfilePrompt, clearOnboardingTimers]);

  useEffect(() => {
    if (!session?.token) return;
    if (!showOnboarding && !debugOnboardingState) return;
    void refreshSessionProfile();
  }, [session?.token, showOnboarding, debugOnboardingState, refreshSessionProfile]);

  useEffect(() => {
    if (!session?.token) return;

    const syncUserInfos = async () => {
      try {
        const response = await nativeAuthService.checkToken(session.token);
        if (!response.valid || !response.user) {
          await refreshSessionProfile(true);
          return;
        }

        const storedRaw = getNativeStorage().getItem(STORAGE_KEYS.USER);

        let storedUser: Record<string, unknown> = session.user as unknown as Record<string, unknown>;
        if (storedRaw) {
          try {
            storedUser = JSON.parse(storedRaw);
          } catch {
            // keep current session user
          }
        }

        const mergedUser = { ...storedUser, ...response.user } as NativeUser;
        persistSessionUser(session.token, mergedUser);

        if (isProfileComplete(mergedUser)) {
          markProfilePromptComplete();
          setShowOnboarding(false);
          setPendingSession(null);
        }
      } catch {
        await refreshSessionProfile(true);
      }
    };

    if (profileSyncTimerRef.current) {
      clearInterval(profileSyncTimerRef.current);
      profileSyncTimerRef.current = null;
    }

    profileSyncTimerRef.current = setInterval(syncUserInfos, PROFILE_SYNC_INTERVAL_MS);
    return () => {
      if (profileSyncTimerRef.current) {
        clearInterval(profileSyncTimerRef.current);
        profileSyncTimerRef.current = null;
      }
    };
  }, [session?.token, session?.user, persistSessionUser, refreshSessionProfile]);

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: COLORS.primary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem 1rem',
  };

  const cardStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '28rem',
    backgroundColor: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '0.75rem',
    boxShadow: COLORS.shadow,
    overflow: 'hidden',
  };

  // Badge-style slider wrapper (shield circle + animated gradient pill)
  const SliderBadge = () => (
    <div style={{ width: '100%', maxWidth: '28rem', marginBottom: '1.5rem' }}>
      {logoUrl ? (
        <img src={logoUrl} alt="Logo" style={{ height: '3rem', margin: '0 auto' }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Shield circle */}
          <div style={{
            width: '3rem',
            height: '3rem',
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: `1px solid ${COLORS.border}`,
          }}>
            <ShieldCheckIcon size={22} color={COLORS.accent} />
          </div>
          {/* Animated gradient pill with slider */}
          <div style={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            borderRadius: '9999px',
            padding: '2px',
            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent}, ${COLORS.primary})`,
            backgroundSize: '200% 100%',
            animation: 'ollaid-border-gradient 3s ease infinite',
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '9999px',
              height: '3rem',
              overflow: 'hidden',
            }}>
              <AppsLogoSlider iamApiUrl={iamApiUrl} speed="normal" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (redirectingTarget) {
    return (
      <div style={containerStyle}>
        <TopBranding subtitle={title} />
        <SliderBadge />

        <div style={cardStyle}>
          <div style={{ padding: '2rem 1.5rem 1.5rem', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: COLORS.cardForeground }}>
              {redirectingReason === 'logout'
                ? 'Déconnexion réussie'
                : redirectingReason === 'already-authenticated'
                  ? 'Session déjà valide'
                  : 'Connexion réussie'}
            </h2>
            <p style={{ fontSize: '0.875rem', color: COLORS.muted, marginTop: '0.5rem' }}>
              Redirection en cours...
            </p>
          </div>
        </div>

        <Footer hideFooter={hideFooter} />
      </div>
    );
  }

  if (session) {
    return (
      <div style={containerStyle}>
        <TopBranding subtitle={title} />
        <SliderBadge />

        <div style={cardStyle}>
          <div style={{ padding: '2rem 1.5rem 1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, textAlign: 'center', color: COLORS.cardForeground }}>
              Bienvenue, {session.user.name}
            </h2>
            <p style={{ fontSize: '0.875rem', color: COLORS.muted, textAlign: 'center', marginTop: '0.25rem' }}>
              Vous êtes connecté à votre compte Ollaid SSO
            </p>
            <div style={{ marginTop: '1.5rem' }}>
              <Button variant="outline" onClick={handleLogout} style={{ width: '100%' }}>
                Déconnexion
              </Button>
            </div>
          </div>
        </div>

        <Footer hideFooter={hideFooter} />

        {(pendingSession || debugOnboardingState) && (
          <OnboardingModal
            open={showOnboarding}
            onOpenChange={(open) => { if (!open) handleOnboardingSkip(); }}
            onDismiss={handleOnboardingDismiss}
            user={(debugOnboardingState || pendingSession)!.user}
            variant={debugOnboardingState ? 'edit' : 'missing'}
            profileHydrating={profileHydrating}
            hydrateProfile={false}
            onUserInfosSync={handleOnboardingUserInfosSync}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
          />
        )}

        {resolvedDebug && (
          <DebugPanel
            saasApiUrl={saasApiUrl}
            iamApiUrl={iamApiUrl}
            onOpenLogin={openDebugLogin}
            onOpenSignup={openDebugSignup}
            onOpenOnboarding={openDebugOnboarding}
            onOpenPassword={openDebugPassword}
            onResetProfilePrompt={resetDebugProfilePrompt}
          />
        )}

        <PasswordRedirectModal
          open={passwordRedirectOpen}
          onOpenChange={setPasswordRedirectOpen}
          saasApiUrl={saasApiUrl}
        />
      </div>
    );
  }

  // Unauthenticated view
  return (
    <div style={containerStyle}>
      <TopBranding subtitle={title} />
      <SliderBadge />

      {/* Main card */}
      <div style={cardStyle}>
        <div style={{ padding: '2rem 1.5rem 1.5rem' }}>
          <p style={{ fontSize: '0.875rem', color: COLORS.muted, textAlign: 'center', marginBottom: '1.5rem' }}>
            {description}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <Button onClick={openLogin} style={{ width: '100%', height: '2.75rem' }}>
              Connexion
            </Button>
            <Button
              variant="outline"
              onClick={openSignup}
              style={{ width: '100%', height: '2.75rem', borderColor: COLORS.cardForeground, color: COLORS.cardForeground }}
            >
              Inscription
            </Button>
          </div>
        </div>
      </div>

      <Footer hideFooter={hideFooter} />

      <LoginModal
        open={modal === 'login'}
        onOpenChange={open => { if (open) openLogin(); else closeModal(); }}
        onSwitchToSignup={switchToSignup}
        onLoginSuccess={handleLoginSuccess}
        saasApiUrl={saasApiUrl}
        iamApiUrl={iamApiUrl}
        logoUrl={logoUrl}
        homeUrl={homeUrl}
        defaultAccountType={accountType}
        initialPhone={loginInitialPhone}
      />

      <SignupModal
        open={modal === 'signup'}
        onOpenChange={open => { if (!open) closeModal(); }}
        onSwitchToLogin={switchToLogin}
        onSwitchToLoginWithPhone={switchToLoginWithPhone}
        onSignupSuccess={handleLoginSuccess}
        saasApiUrl={saasApiUrl}
        iamApiUrl={iamApiUrl}
        logoUrl={logoUrl}
        homeUrl={homeUrl}
        defaultAccountType={accountType}
      />

      {(pendingSession || debugOnboardingState) && (
      <OnboardingModal
        open={showOnboarding}
        onOpenChange={(open) => { if (!open) handleOnboardingSkip(); }}
        onDismiss={handleOnboardingDismiss}
        user={(debugOnboardingState || pendingSession)!.user}
        variant={debugOnboardingState ? 'edit' : 'missing'}
        profileHydrating={profileHydrating}
        hydrateProfile={false}
        onUserInfosSync={handleOnboardingUserInfosSync}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
      )}

      {resolvedDebug && (
        <DebugPanel
          saasApiUrl={saasApiUrl}
          iamApiUrl={iamApiUrl}
          onOpenLogin={openDebugLogin}
          onOpenSignup={openDebugSignup}
          onOpenOnboarding={openDebugOnboarding}
          onOpenPassword={openDebugPassword}
          onResetProfilePrompt={resetDebugProfilePrompt}
        />
      )}

      <PasswordRedirectModal
        open={passwordRedirectOpen}
        onOpenChange={setPasswordRedirectOpen}
        saasApiUrl={saasApiUrl}
      />
    </div>
  );
}

export default NativeSSOPage;
